import { useCallback, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

// ============================================
// COMPREHENSIVE WIDGET TRACKING HOOK
// Integrates with /phcrm backend for:
// - Lead creation & management
// - Conversation storage
// - Analytics events
// - Contact capture
// ============================================

interface VisitorData {
  user_agent: string;
  language: string;
  timezone: string;
  referrer: string;
  current_url: string;
  device_type: "mobile" | "tablet" | "desktop";
  browser: string;
  os: string;
  screen_resolution: string;
  viewport_size: string;
}

interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

interface TrackingEvent {
  event_type: string;
  properties?: Record<string, unknown>;
}

interface Message {
  id: string;
  sender: "user" | "bot" | "agent";
  text: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

const generateUUID = (): string => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem("ph_widget_session_id");
  if (!sessionId) {
    sessionId = generateUUID();
    sessionStorage.setItem("ph_widget_session_id", sessionId);
  }
  return sessionId;
};

const getDeviceType = (): "mobile" | "tablet" | "desktop" => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) return "tablet";
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) return "mobile";
  return "desktop";
};

const getBrowserInfo = (): string => {
  const ua = navigator.userAgent;
  if (ua.indexOf("Firefox") > -1) return "Firefox";
  if (ua.indexOf("Edg") > -1) return "Edge";
  if (ua.indexOf("Chrome") > -1) return "Chrome";
  if (ua.indexOf("Safari") > -1) return "Safari";
  if (ua.indexOf("Opera") > -1 || ua.indexOf("OPR") > -1) return "Opera";
  return "Unknown";
};

const getOSInfo = (): string => {
  const ua = navigator.userAgent;
  if (ua.indexOf("Win") > -1) return "Windows";
  if (ua.indexOf("Mac") > -1) return "MacOS";
  if (ua.indexOf("Linux") > -1) return "Linux";
  if (ua.indexOf("Android") > -1) return "Android";
  if (ua.indexOf("iPhone") > -1 || ua.indexOf("iPad") > -1) return "iOS";
  return "Unknown";
};

const getVisitorData = (): VisitorData => ({
  user_agent: navigator.userAgent,
  language: navigator.language,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  referrer: document.referrer,
  current_url: window.location.href,
  device_type: getDeviceType(),
  browser: getBrowserInfo(),
  os: getOSInfo(),
  screen_resolution: `${window.screen.width}x${window.screen.height}`,
  viewport_size: `${window.innerWidth}x${window.innerHeight}`,
});

const getUtmParams = (): UtmParams => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || undefined,
    utm_medium: params.get("utm_medium") || undefined,
    utm_campaign: params.get("utm_campaign") || undefined,
    utm_term: params.get("utm_term") || undefined,
    utm_content: params.get("utm_content") || undefined,
  };
};

// ============================================
// INTENT DETECTION FOR AGENT TRANSFER
// ============================================
const TRANSFER_TRIGGERS = [
  "hablar con persona",
  "hablar con alguien",
  "agente humano",
  "persona real",
  "hablar con un humano",
  "necesito un agente",
  "agente",
  "humano",
  "es urgente",
  "urgente",
  "no me ayuda",
  "no entiendes",
  "esto es complicado",
  "necesito ayuda real",
  "quiero llamar",
  "puedo llamar",
  "atención al cliente",
  "representante",
];

const HIGH_VALUE_TRIGGERS = [
  "precio",
  "cuánto cuesta",
  "cuanto cuesta",
  "tarifa",
  "contratar",
  "agendar",
  "cita",
  "consulta",
  "reunión",
  "empresa",
  "crear empresa",
  "retorno",
  "pensión",
  "2 pilar",
  "segundo pilar",
];

interface IntentResult {
  needsAgent: boolean;
  isHighValue: boolean;
  topics: string[];
}

export const detectUserIntent = (message: string): IntentResult => {
  const lowerMessage = message.toLowerCase();
  
  const needsAgent = TRANSFER_TRIGGERS.some(trigger => lowerMessage.includes(trigger));
  const isHighValue = HIGH_VALUE_TRIGGERS.some(trigger => lowerMessage.includes(trigger));
  
  // Detect topics for analytics
  const topicPatterns: Record<string, string[]> = {
    permisos: ["permiso", "residencia", "visa", "permiso b", "permiso c", "permiso l"],
    seguros: ["seguro", "lamal", "kvg", "kranken", "salud", "krankenkasse"],
    idiomas: ["alemán", "aleman", "inglés", "ingles", "curso", "clase", "deutsch"],
    gestoria: ["gestoria", "gestoría", "rav", "desempleo", "paro", "trabajo"],
    legal: ["legal", "abogado", "divorcio", "contrato", "reclamación", "demanda"],
    vivienda: ["piso", "apartamento", "vivienda", "habitación", "alquiler", "casa", "wohnung"],
    digital: ["web", "página", "crm", "chatbot", "marketing", "seo", "app"],
    retorno: ["volver", "retorno", "regresar", "pensión", "pilar", "ahv", "bvg"],
    relocation: ["mudarme", "relocate", "llegar a suiza", "pack", "mudanza"],
    coches: ["coche", "vehículo", "alquiler coche", "furgoneta"],
  };
  
  const topics: string[] = [];
  for (const [topic, patterns] of Object.entries(topicPatterns)) {
    if (patterns.some(pattern => lowerMessage.includes(pattern))) {
      topics.push(topic);
    }
  }
  
  return { needsAgent, isHighValue, topics };
};

const extractTopicFromMessage = (text: string): string => {
  const result = detectUserIntent(text);
  return result.topics[0] || "general";
};

// ============================================
// MAIN HOOK
// ============================================

export const useWidgetTracking = () => {
  const sessionId = useRef<string>(getSessionId());
  const leadId = useRef<string | null>(sessionStorage.getItem("ph_widget_lead_id"));
  const conversationId = useRef<string | null>(sessionStorage.getItem("ph_widget_conversation_id"));
  const conversationStartTime = useRef<number | null>(null);
  const utmParams = useRef<UtmParams>(getUtmParams());
  const visitorData = useRef<VisitorData>(getVisitorData());
  const isInitialized = useRef<boolean>(false);

  // Initialize on mount
  useEffect(() => {
    if (!isInitialized.current) {
      utmParams.current = getUtmParams();
      visitorData.current = getVisitorData();
      isInitialized.current = true;
    }
  }, []);

  // ============================================
  // TRACK ANALYTICS EVENT
  // ============================================
  const trackEvent = useCallback(async (event: TrackingEvent): Promise<boolean> => {
    try {
      const metadataPayload = JSON.parse(JSON.stringify({
        ...event.properties,
        lead_id: leadId.current,
        conversation_id: conversationId.current,
        visitor_data: visitorData.current,
        page_url: window.location.href,
        page_title: document.title,
      }));

      const { error } = await supabase.from("customer_interactions").insert({
        interaction_type: `widget_${event.event_type}`,
        source_page: window.location.pathname,
        session_id: sessionId.current,
        utm_source: utmParams.current.utm_source || null,
        utm_medium: utmParams.current.utm_medium || null,
        utm_campaign: utmParams.current.utm_campaign || null,
        metadata: metadataPayload,
        status: "new",
      });

      if (error) {
        console.error("[Widget Tracking] Error tracking event:", error);
        return false;
      }

      console.log(`[Widget Tracking] Event tracked: ${event.event_type}`);
      return true;
    } catch (err) {
      console.error("[Widget Tracking] Error in trackEvent:", err);
      return false;
    }
  }, []);

  // ============================================
  // CREATE LEAD
  // ============================================
  const createLead = useCallback(async (): Promise<string | null> => {
    // If lead already exists, return it
    if (leadId.current) {
      return leadId.current;
    }

    try {
      const { data, error } = await supabase.functions.invoke("track-customer-interaction", {
        body: {
          interaction_type: "widget_opened",
          source_page: window.location.pathname,
          session_id: sessionId.current,
          utm_source: utmParams.current.utm_source,
          utm_medium: utmParams.current.utm_medium,
          utm_campaign: utmParams.current.utm_campaign,
          metadata: {
            widget_type: "help-widget",
            visitor_data: visitorData.current,
            selected_service: sessionStorage.getItem("selected_service"),
          },
        },
      });

      if (error) {
        console.error("[Widget Tracking] Error creating lead:", error);
        return null;
      }

      const newLeadId = data?.interaction_id;
      if (newLeadId) {
        leadId.current = newLeadId;
        sessionStorage.setItem("ph_widget_lead_id", newLeadId);
        console.log("[Widget Tracking] Lead created:", newLeadId);
      }

      return newLeadId;
    } catch (err) {
      console.error("[Widget Tracking] Error in createLead:", err);
      return null;
    }
  }, []);

  // ============================================
  // UPDATE LEAD WITH CONTACT INFO
  // ============================================
  const updateLeadContact = useCallback(
    async (email?: string, phone?: string, name?: string): Promise<boolean> => {
      if (!email && !phone) return false;

      try {
        // Track contact capture event
        await supabase.functions.invoke("track-customer-interaction", {
          body: {
            interaction_type: "contact_captured",
            source_page: window.location.pathname,
            customer_email: email,
            customer_phone: phone,
            customer_name: name,
            session_id: sessionId.current,
            metadata: {
              lead_id: leadId.current,
              conversation_id: conversationId.current,
              capture_method: "chat_widget",
            },
          },
        });

        console.log("[Widget Tracking] Contact captured:", { email, phone });
        return true;
      } catch (err) {
        console.error("[Widget Tracking] Error updating lead contact:", err);
        return false;
      }
    },
    []
  );

  // ============================================
  // CREATE CONVERSATION
  // ============================================
  const createConversation = useCallback(
    async (type: "chat-ia" | "live-chat"): Promise<string | null> => {
      try {
        // Generate conversation ID
        const newConversationId = generateUUID();
        conversationId.current = newConversationId;
        conversationStartTime.current = Date.now();
        sessionStorage.setItem("ph_widget_conversation_id", newConversationId);
        sessionStorage.setItem("ph_conversation_start_time", Date.now().toString());

        // Prepare messages and metadata as JSON-safe objects
        const initialMessages = JSON.parse(JSON.stringify([
          {
            id: generateUUID(),
            sender: "bot",
            text: getWelcomeMessage(),
            timestamp: new Date().toISOString(),
          },
        ]));

        const conversationMetadata = JSON.parse(JSON.stringify({
          type,
          lead_id: leadId.current,
          visitor_data: visitorData.current,
          utm_params: utmParams.current,
          source_page: window.location.pathname,
        }));

        // Create conversation in ai_conversations table
        const { error } = await supabase.from("ai_conversations").insert({
          session_id: sessionId.current,
          messages: initialMessages,
          metadata: conversationMetadata,
          converted_to_lead: false,
          total_messages: 1,
        });

        if (error) {
          console.error("[Widget Tracking] Error creating conversation:", error);
          return null;
        }

        // Track conversation started event
        await trackEvent({
          event_type: "conversation_started",
          properties: {
            conversation_type: type,
            conversation_id: newConversationId,
          },
        });

        console.log("[Widget Tracking] Conversation created:", newConversationId);
        return newConversationId;
      } catch (err) {
        console.error("[Widget Tracking] Error in createConversation:", err);
        return null;
      }
    },
    [trackEvent]
  );

  // ============================================
  // SAVE MESSAGE TO CONVERSATION
  // ============================================
  const saveMessage = useCallback(
    async (message: Message): Promise<boolean> => {
      if (!sessionId.current) return false;

      try {
        // Get current conversation
        const { data: conversation, error: fetchError } = await supabase
          .from("ai_conversations")
          .select("id, messages, total_messages")
          .eq("session_id", sessionId.current)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (fetchError || !conversation) {
          console.error("[Widget Tracking] No conversation found to save message");
          return false;
        }

        // Append message - parse existing messages safely
        const existingMessages = Array.isArray(conversation.messages) 
          ? conversation.messages 
          : [];
        const updatedMessages = JSON.parse(JSON.stringify([...existingMessages, message]));

        const { error: updateError } = await supabase
          .from("ai_conversations")
          .update({
            messages: updatedMessages,
            total_messages: updatedMessages.length,
            updated_at: new Date().toISOString(),
          })
          .eq("id", conversation.id);

        if (updateError) {
          console.error("[Widget Tracking] Error saving message:", updateError);
          return false;
        }

        // Track message sent event
        await trackEvent({
          event_type: "message_sent",
          properties: {
            sender: message.sender,
            message_length: message.text.length,
            conversation_id: conversationId.current,
          },
        });

        // Check for email/phone in message and capture
        const emailMatch = message.text.match(/[\w.-]+@[\w.-]+\.\w+/);
        const phoneMatch = message.text.match(/\+?\d{9,}/);
        if (emailMatch || phoneMatch) {
          await updateLeadContact(
            emailMatch ? emailMatch[0] : undefined,
            phoneMatch ? phoneMatch[0] : undefined
          );
        }

        return true;
      } catch (err) {
        console.error("[Widget Tracking] Error in saveMessage:", err);
        return false;
      }
    },
    [trackEvent, updateLeadContact]
  );

  // ============================================
  // TRACK OPTION SELECTION
  // ============================================
  const trackOptionSelected = useCallback(
    async (option: "chat-ia" | "live-chat" | "whatsapp"): Promise<void> => {
      await trackEvent({
        event_type: "option_selected",
        properties: {
          selected_option: option,
          lead_id: leadId.current,
        },
      });

      // Update lead with selected widget type
      await supabase.functions.invoke("track-customer-interaction", {
        body: {
          interaction_type: `widget_${option}_selected`,
          source_page: window.location.pathname,
          session_id: sessionId.current,
          metadata: {
            lead_id: leadId.current,
            widget_type: option,
          },
        },
      });
    },
    [trackEvent]
  );

  // ============================================
  // TRACK WHATSAPP CLICK
  // ============================================
  const trackWhatsAppClick = useCallback(async (): Promise<void> => {
    await supabase.functions.invoke("track-customer-interaction", {
      body: {
        interaction_type: "whatsapp_click",
        source_page: window.location.pathname,
        session_id: sessionId.current,
        metadata: {
          lead_id: leadId.current,
          phone_number: "41797700342",
          source: "help_widget",
        },
      },
    });
  }, []);

  // ============================================
  // TRACK QUICK REPLY CLICK
  // ============================================
  const trackQuickReplyClick = useCallback(
    async (buttonText: string): Promise<void> => {
      const topic = extractTopicFromMessage(buttonText);

      await trackEvent({
        event_type: "quick_reply_clicked",
        properties: {
          button_text: buttonText,
          topic,
          conversation_id: conversationId.current,
        },
      });

      // Update lead with interest topic
      await supabase.functions.invoke("track-customer-interaction", {
        body: {
          interaction_type: "topic_interest",
          source_page: window.location.pathname,
          session_id: sessionId.current,
          metadata: {
            lead_id: leadId.current,
            topic,
            button_text: buttonText,
          },
        },
      });
    },
    [trackEvent]
  );

  // ============================================
  // CLOSE CONVERSATION
  // ============================================
  const closeConversation = useCallback(
    async (messageCount: number): Promise<void> => {
      if (!sessionId.current) return;

      const startTime = sessionStorage.getItem("ph_conversation_start_time");
      const durationSeconds = startTime
        ? Math.floor((Date.now() - parseInt(startTime)) / 1000)
        : 0;

      await trackEvent({
        event_type: "conversation_ended",
        properties: {
          conversation_id: conversationId.current,
          duration_seconds: durationSeconds,
          message_count: messageCount,
          user_initiated: true,
        },
      });

      // Clear conversation from session
      sessionStorage.removeItem("ph_widget_conversation_id");
      sessionStorage.removeItem("ph_conversation_start_time");
      conversationId.current = null;
      conversationStartTime.current = null;
    },
    [trackEvent]
  );

  // ============================================
  // TRACK WIDGET MINIMIZED
  // ============================================
  const trackWidgetMinimized = useCallback(async (): Promise<void> => {
    await trackEvent({
      event_type: "widget_minimized",
      properties: {
        lead_id: leadId.current,
      },
    });
  }, [trackEvent]);

  // ============================================
  // TRACK WIDGET EXPANDED
  // ============================================
  const trackWidgetExpanded = useCallback(async (): Promise<void> => {
    await trackEvent({
      event_type: "widget_expanded",
      properties: {
        lead_id: leadId.current,
      },
    });
  }, [trackEvent]);

  // ============================================
  // GET WELCOME MESSAGE (personalized)
  // ============================================

  return {
    // IDs
    sessionId: sessionId.current,
    leadId: leadId.current,
    conversationId: conversationId.current,

    // Lead management
    createLead,
    updateLeadContact,

    // Conversation management
    createConversation,
    saveMessage,
    closeConversation,

    // Event tracking
    trackEvent,
    trackOptionSelected,
    trackWhatsAppClick,
    trackQuickReplyClick,
    trackWidgetMinimized,
    trackWidgetExpanded,

    // Utilities
    generateUUID,
  };
};

// ============================================
// HELPER: Get personalized welcome message
// ============================================
const getWelcomeMessage = (): string => {
  const service = sessionStorage.getItem("selected_service");
  const messages: Record<string, string> = {
    gestoria: "¡Hola! 👋 Veo que te interesan nuestros servicios de gestoría. ¿En qué puedo ayudarte?",
    "asesoria-legal": "¡Hola! 👋 Te ayudo con consultas de asesoría legal. ¿Qué necesitas saber?",
    legal: "¡Hola! 👋 Te ayudo con consultas de asesoría legal. ¿Qué necesitas saber?",
    seguros: "¡Hola! 👋 ¿Necesitas información sobre seguros en Suiza? Te ayudo a comparar opciones.",
    idiomas: "¡Hola! 👋 ¿Te interesa aprender alemán o inglés? Te cuento sobre nuestros cursos.",
    vivienda: "¡Hola! 👋 ¿Buscas alojamiento en Zürich? Te ayudo a encontrar opciones.",
    relocation: "¡Hola! 👋 ¿Acabas de llegar a Suiza? Te cuento sobre nuestros packs de bienvenida.",
    digital: "¡Hola! 👋 ¿Necesitas soluciones digitales para tu empresa? Web, CRM, IA... ¡Te ayudo!",
    desempleo: "¡Hola! 👋 ¿Necesitas ayuda con la inscripción en el RAV? Te explico el proceso.",
    rav: "¡Hola! 👋 ¿Necesitas ayuda con la inscripción en el RAV? Te explico el proceso.",
    retorno: "¡Hola! 👋 ¿Planeas volver a tu país? Te ayudo con las gestiones de retorno.",
    coches: "¡Hola! 👋 ¿Necesitas alquilar un vehículo o furgoneta? Te cuento las opciones.",
    traducciones: "¡Hola! 👋 ¿Necesitas traducir documentos? Trabajamos con alemán, inglés, francés, italiano y portugués.",
    cv: "¡Hola! 👋 ¿Necesitas crear o actualizar tu CV al formato suizo? ¡Te ayudo!",
  };
  return messages[service || ""] || "¡Hola! 👋 Soy el asistente de Punto Hispano. ¿En qué puedo ayudarte hoy?";
};

export default useWidgetTracking;
